import { memo, useId } from 'react';
import type { CSSProperties } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';

import { Button } from '../buttons';
import Icon from '../Icon';
import { SimpleCheckboxInput } from '../forms';

import type { InputColumn } from './dataTableV2Utils';

interface ColumnChooserProps {
  columns: InputColumn[];
  hiddenNames: Set<string>;
  onToggleColumn: (columnName: string, visible: boolean) => void;
}

/** Максимальная высота списка колонок — дальше скролл (как у HeaderFilter). */
const CHOOSER_LIST_STYLE: CSSProperties = { maxHeight: '320px', overflowY: 'auto', minWidth: '240px' };

/**
 * Меню показа/скрытия столбцов (замена dx ColumnChooser).
 * OverlayTrigger + Popover вместо Dropdown: в react-bootstrap 1.6 нет autoClose,
 * а здесь клик по чекбоксу не должен закрывать список (rootClose — только клик мимо).
 */
const ColumnChooser = memo(({ columns, hiddenNames, onToggleColumn }: ColumnChooserProps) => {
  const popoverId = useId();

  const popover = (
    <Popover id={ popoverId } style={ CHOOSER_LIST_STYLE }>
      <Popover.Title>Столбцы</Popover.Title>
      <Popover.Content>
        { columns.map(column => (
          <SimpleCheckboxInput
            key={ column.name }
            label={ column.title ?? column.name }
            checked={ !hiddenNames.has(column.name) }
            disabled={ column.togglingEnabled === false }
            rootClassName='py-1'
            noMarginBottom
            onChange={ event => onToggleColumn(column.name, event.target.checked) }
          />
        )) }
      </Popover.Content>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger='click'
      rootClose
      placement='bottom-end'
      container={ document.body }
      overlay={ popover }
    >
      <Button
        variant='outline-secondary'
        className='border-0'
        title='Показать/скрыть столбцы'
      >
        <Icon name='columns' />
      </Button>
    </OverlayTrigger>
  );
});

ColumnChooser.displayName = 'DataTableV2ColumnChooser';

export default ColumnChooser;
